import React, { useEffect, useState } from "react";
import classes from "./SavingsAccount.module.css";
import HeaderNav from "../../Components/Dahboard Header/HeaderNav/HeaderNav";
import { useLocation, useNavigate } from "react-router-dom";
import searchicon from "../../Assets/searchicon.png";
import Footer from "../../Components/Footer/Footer";
import { Eye, Edit, Trash } from 'lucide-react';
import api from "../../Components/API/Api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Swal from 'sweetalert2';
import SavingAccountModal from "./SavingModal/SavingAccountModal";
 
import UploadExcelModal2 from "./SavingModal/UploadExcelModal2";

const SavingsAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // --- DATA STATES ---
  const [savingsAccounts, setSavingsAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // --- MODAL STATES ---
  const [openCashReciept, setOpenCashReciept] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  
  // --- CRUD ACTION STATES ---
  const [editingSavings, setEditingSavings] = useState(null);
  const [viewingSavings, setViewingSavings] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // ✅ COMPANY ID (Properly fetched from localStorage)
  const [companyId, setCompanyId] = useState(null); 

  const pageMap = {
    "/dashboard": "Dashboard",
    "/transactions": "Transactions",
    "/accounts": "Accounting",
    "/reports": "Reports",
    "/banking": "Banking",
    "/settings": "Settings",
  };

  // --- FETCH SAVINGS ACCOUNTS ---
  const fetchSavingsAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/account/fetch-all-members-savings");
      const savings = res.data?.data?.savings?.data;
      if (res.data?.status && Array.isArray(savings)) {
        setSavingsAccounts(savings);
      } else {
        setSavingsAccounts([]);
      }
    } catch (error) {
      console.error(error);
      setSavingsAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavingsAccounts();
    
    // ✅ Parse company info from localStorage
    const compInfo = localStorage.getItem('companyInfo');
    if (compInfo) {
      try {
        const parsedInfo = JSON.parse(compInfo);
        setCompanyId(parsedInfo.id);
      } catch (e) {
        console.error("Error parsing company info", e);
      }
    }
  }, []);

  // --- SEARCH FUNCTIONALITY ---
  const filteredSavings = savingsAccounts.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      (item.member?.name || "").toLowerCase().includes(term) ||
      (item.prefix || "").toLowerCase().includes(term) ||
      (item.type?.product_name || "").toLowerCase().includes(term) ||
      (item.transaction_date || "").toLowerCase().includes(term)
    );
  });

  const handleSearch = (e) => setSearchTerm(e.target.value);

  // --- VIEW (Eye) ---
  const handleViewClick = (item) => {
    setViewingSavings(item);
    setIsReadOnly(true);
    setOpenCashReciept(true);
  };

  // --- EDIT (Pen) ---
  const handleEditClick = (item) => {
    setEditingSavings(item);
    setIsReadOnly(false);
    setOpenCashReciept(true);
  };

  // --- DELETE (Trash) ---
  const handleDeleteSavings = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Savings Account?',
      text: 'This action cannot be undone. Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/v1/account/delete-savings/${id}`);
        Swal.fire('Deleted!', 'Savings account has been deleted.', 'success');
        fetchSavingsAccounts(); // refresh list
      } catch (error) {
        Swal.fire('Error!', error.response?.data?.message || 'Failed to delete.', 'error');
      }
    }
  };

  // --- CLOSE MODALS & RESET STATE ---
  const closeCashReceiptModal = () => {
    setOpenCashReciept(false);
    setEditingSavings(null);
    setViewingSavings(null);
    setIsReadOnly(false);
  };

  const formatCurrency = (value) =>
    Number(value).toLocaleString('en-NG', { minimumFractionDigits: 2 });

  return (
    <div>
      <HeaderNav activePage={pageMap[location.pathname]} />

      <div className={classes.Container}>
        <header className={classes.Header}>
          <h2 className={classes.Title}>Savings Account</h2>
          <h1 className={classes.Titles}>
            View and Manage Savings accounts of members.
          </h1>
        </header>

        <div className={classes.headerActions}>
          <div className={classes.actionsRight}>
            <div className={classes.inputWrapper}>
              <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
              <input
                type="text"
                className={classes.searchBar}
                placeholder="Search savings"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
          
          {/* ✨ ACTION BUTTONS SECTION (UPDATED ORDER) */}
          <div className={classes.actionButtons}>
            
            {/* 1. Upload Excel Button (Moved to front, outline style) */}
            <button
              className={classes.btnUploadExcel} 
              onClick={() => setIsUploadModalOpen(true)}
            >
              + Upload Excel
            </button>

            {/* 2. New Savings Account Button */}
            <button
              className={classes.btnss}
              onClick={() => {
                setEditingSavings(null);
                setViewingSavings(null);
                setIsReadOnly(false);
                setOpenCashReciept(true);
              }}
            >
              + New Savings Account
            </button>
          </div>
        </div>

        <div className={classes.invoicecont}>
          <table className={classes.invoiceTable}>
            <thead style={{ whiteSpace: "nowrap" }}>
              <tr>
                <th>S/N</th>
                <th>Savings Code</th>
                <th>Members Name</th>
                <th>Product Name</th>
                <th>Transaction Date</th>
                <th>Opening Deposit</th>
                <th>Monthly Savings</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill().map((_, idx) => (
                  <tr key={idx}>
                    {Array(8).fill().map((_, i) => (
                      <td key={i}>
                        <Skeleton height={20} style={{ marginBottom: 10 }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredSavings.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>
                    No savings accounts found.
                  </td>
                </tr>
              ) : (
                filteredSavings.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td>{item.prefix}</td>
                    <td>{item.member?.name}</td>
                    <td>{item.type?.product_name}</td>
                    <td>{item.transaction_date}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.opening_deposit)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                    <td className={classes.tableCell}>
                      <div className={classes.actionIcons}>
                        {/* View */}
                        <Eye
                          size={16}
                          style={{ color: 'rgba(22, 163, 74, 1)', cursor: 'pointer' }}
                          onClick={() => handleViewClick(item)}
                        />
                        {/* Edit */}
                        <Edit
                          size={16}
                          style={{ margin: '0 8px', color: 'rgba(37, 99, 235, 1)', cursor: 'pointer' }}
                          onClick={() => handleEditClick(item)}
                        />
                        {/* Delete */}
                        <Trash
                          size={16}
                          style={{ color: 'rgb(255, 2, 2)', cursor: 'pointer' }}
                          onClick={() => handleDeleteSavings(item.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Static pagination */}
          <div className={classes.paginationContainer}>
            <div className={classes.paginationControls}>
              <button>First</button>
              <button className={classes.active}>1</button>
              <button>Last</button>
              <span className={classes.pageText}>
                Displaying 1 - {filteredSavings.length} of {filteredSavings.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* --- MODALS --- */}
      
      {/* 1. Savings Account Modal (Create / Edit / View) */}
      <SavingAccountModal
        isOpen={openCashReciept}
        onClose={closeCashReceiptModal}
        initialData={editingSavings || viewingSavings}
        isReadOnly={isReadOnly}
        onSuccess={fetchSavingsAccounts}
      />

      {/* 2. Upload Excel Modal (2-Step Bulk Upload) */}
      <UploadExcelModal2
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        companyId={companyId} // ✅ Passes actual company ID
        onSuccess={fetchSavingsAccounts} // ✅ Refreshes table on success
      />

      <Footer />
    </div>
  );
};

export default SavingsAccount;